/**
 * RFC 4122 UUID v5 (SHA-1 name-based) — pure JS, no Node crypto.
 * Used for deterministic legacy-import entity ids.
 */
const HEX = "0123456789abcdef";
function bytesToUuid(bytes) {
    const b = [];
    for (let i = 0; i < 16; i++) {
        if (i === 4 || i === 6 || i === 8 || i === 10)
            b.push("-");
        b.push(HEX[bytes[i] >> 4], HEX[bytes[i] & 0xf]);
    }
    return b.join("");
}
function parseUuid(uuid) {
    const hex = uuid.replace(/-/g, "").toLowerCase();
    if (hex.length !== 32)
        throw new Error(`invalid uuid: ${uuid}`);
    const out = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
        out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
}
/** Minimal SHA-1 (FIPS 180-1) for uuidv5 only. */
function sha1(message) {
    const ml = message.length;
    let newLen = ml + 1;
    while (newLen % 64 !== 56)
        newLen++;
    const buf = new Uint8Array(newLen + 8);
    buf.set(message);
    buf[ml] = 0x80;
    const bitLen = ml * 8;
    const view = new DataView(buf.buffer);
    view.setUint32(buf.length - 8, Math.floor(bitLen / 0x100000000), false);
    view.setUint32(buf.length - 4, bitLen >>> 0, false);
    let h0 = 0x67452301;
    let h1 = 0xefcdab89;
    let h2 = 0x98badcfe;
    let h3 = 0x10325476;
    let h4 = 0xc3d2e1f0;
    const w = new Int32Array(80);
    for (let offset = 0; offset < buf.length; offset += 64) {
        for (let i = 0; i < 16; i++) {
            w[i] = view.getInt32(offset + i * 4, false);
        }
        for (let i = 16; i < 80; i++) {
            const x = (w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16]) >>> 0;
            w[i] = ((x << 1) | (x >>> 31)) | 0;
        }
        let a = h0;
        let b = h1;
        let c = h2;
        let d = h3;
        let e = h4;
        for (let i = 0; i < 80; i++) {
            let f;
            let k;
            if (i < 20) {
                f = (b & c) | (~b & d);
                k = 0x5a827999;
            }
            else if (i < 40) {
                f = b ^ c ^ d;
                k = 0x6ed9eba1;
            }
            else if (i < 60) {
                f = (b & c) | (b & d) | (c & d);
                k = 0x8f1bbcdc;
            }
            else {
                f = b ^ c ^ d;
                k = 0xca62c1d6;
            }
            const temp = ((((a << 5) | (a >>> 27)) + f + e + k + (w[i] >>> 0)) | 0);
            e = d;
            d = c;
            c = ((b << 30) | (b >>> 2)) | 0;
            b = a;
            a = temp;
        }
        h0 = (h0 + a) | 0;
        h1 = (h1 + b) | 0;
        h2 = (h2 + c) | 0;
        h3 = (h3 + d) | 0;
        h4 = (h4 + e) | 0;
    }
    const out = new Uint8Array(20);
    const ov = new DataView(out.buffer);
    ov.setInt32(0, h0, false);
    ov.setInt32(4, h1, false);
    ov.setInt32(8, h2, false);
    ov.setInt32(12, h3, false);
    ov.setInt32(16, h4, false);
    return out;
}
/** UTF-8 encode without depending on DOM TextEncoder types. */
function utf8Encode(s) {
    const out = [];
    for (let i = 0; i < s.length; i++) {
        let c = s.charCodeAt(i);
        if (c < 0x80) {
            out.push(c);
        }
        else if (c < 0x800) {
            out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
        }
        else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            const c2 = s.charCodeAt(++i);
            const cp = 0x10000 + ((c & 0x3ff) << 10) + (c2 & 0x3ff);
            out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
        }
        else {
            out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
        }
    }
    return Uint8Array.from(out);
}
/** UUID v5 from a namespace UUID string and a name. */
export function uuidv5(name, namespaceUuid) {
    const ns = parseUuid(namespaceUuid);
    const nameBytes = utf8Encode(name);
    const joined = new Uint8Array(ns.length + nameBytes.length);
    joined.set(ns, 0);
    joined.set(nameBytes, ns.length);
    const hash = sha1(joined);
    const bytes = hash.slice(0, 16);
    bytes[6] = (bytes[6] & 0x0f) | 0x50;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return bytesToUuid(bytes);
}
//# sourceMappingURL=uuidv5.js.map